/**
 * User-related badge primitives — reused across Users list, detail page, and wizards.
 *
 * All badges are theme-safe (work in both light and dark mode) and follow the
 * platform policy of icon + text (never color alone).
 */

import { Lock, Shield, ShieldCheck, ShieldOff, UserCheck, UserX, KeyRound, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  type UserStatus, type ClearanceLevel, type EmploymentType, type MfaMethod, type ClassificationTier,
  type RoleSource,
  USER_STATUS_STYLES, CLEARANCE_STYLES, EMPLOYMENT_TYPE_STYLES, MFA_METHOD_LABEL, CLASSIFICATION_LABEL,
  RISK_SCORE_STYLES,
} from '@/lib/mock-users-data';

// ─── Status ──────────────────────────────────────────────────────────────────

export function UserStatusBadge({ status, className }: { status: UserStatus; className?: string }) {
  const s = USER_STATUS_STYLES[status];
  return (
    <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', s.color, className)}>
      {s.label}
    </Badge>
  );
}

// ─── Clearance ──────────────────────────────────────────────────────────────

export function ClearanceBadge({
  level, className, showUnclassified = false,
}: {
  level: ClearanceLevel;
  className?: string;
  /** If false (default), hide badge when level === 'unclassified'. */
  showUnclassified?: boolean;
}) {
  if (level === 'unclassified' && !showUnclassified) return null;
  const s = CLEARANCE_STYLES[level];
  return (
    <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5 gap-1', s.color, className)}>
      <Shield className="h-2.5 w-2.5" />
      {s.label}
    </Badge>
  );
}

// ─── Employment type ────────────────────────────────────────────────────────

/**
 * B2c-T2 — `type` is NULLABLE, and a null renders NOTHING.
 *
 * `users.employment_type` allows NULL, and the deleted translation map in
 * `use-users.ts` hid that behind `default: return 'fte'` — a person whose
 * employment type had never been set wore an "FTE" badge. Indexing
 * `EMPLOYMENT_TYPE_STYLES` with an absent value would have thrown here
 * instead, so the honest answer is no badge at all.
 */
export function EmploymentTypeBadge({ type, className }: { type: EmploymentType | null | undefined; className?: string }) {
  const s = type ? EMPLOYMENT_TYPE_STYLES[type] : undefined;
  if (!s) return null;
  return (
    <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', s.color, className)}>
      {s.label}
    </Badge>
  );
}

// ─── US Person ──────────────────────────────────────────────────────────────

export function UsPersonIndicator({
  verified, compact = false, className,
}: {
  verified: boolean;
  /** If true, renders only an icon. Otherwise icon + label. */
  compact?: boolean;
  className?: string;
}) {
  const Icon = verified ? UserCheck : UserX;
  const colorClass = verified
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-amber-600 dark:text-amber-400';
  const label = verified ? 'US Person' : 'Unverified';
  const tooltipText = verified
    ? 'US Person verification on file (ITAR §120.15)'
    : 'US Person status not verified — ITAR-restricted content inaccessible';

  if (compact) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn('inline-flex items-center', colorClass, className)}>
              <Icon className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="text-xs">{tooltipText}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <span
      className={cn('inline-flex items-center gap-1 text-[11px] font-medium', colorClass, className)}
      title={tooltipText}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

// ─── MFA ────────────────────────────────────────────────────────────────────

export function MfaStatusChip({
  enabled, method, enforced = false, compact = false, className,
}: {
  enabled: boolean;
  method: MfaMethod;
  enforced?: boolean;
  compact?: boolean;
  className?: string;
}) {
  const label = enabled ? MFA_METHOD_LABEL[method] : 'Not enrolled';
  const color = enabled
    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
    : 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30';

  if (compact) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                'inline-flex items-center justify-center h-5 w-5 rounded-full border',
                color,
                className,
              )}
            >
              {enabled ? <KeyRound className="h-2.5 w-2.5" /> : <ShieldOff className="h-2.5 w-2.5" />}
            </span>
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            {label}
            {enforced && ' · Required by policy'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5 gap-1', color, className)}>
      {enabled ? <KeyRound className="h-2.5 w-2.5" /> : <ShieldOff className="h-2.5 w-2.5" />}
      {label}
      {enforced && <Lock className="h-2.5 w-2.5 ml-0.5 opacity-70" />}
    </Badge>
  );
}

// ─── Classification access ──────────────────────────────────────────────────

export function ClassificationBadge({
  tier, className,
}: {
  tier: ClassificationTier;
  className?: string;
}) {
  const colors: Record<ClassificationTier, string> = {
    unclassified: 'bg-muted text-muted-foreground border-border',
    cui:          'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    restricted:   'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
  };
  return (
    <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', colors[tier], className)}>
      {CLASSIFICATION_LABEL[tier]}
    </Badge>
  );
}

// ─── Risk score ─────────────────────────────────────────────────────────────

export function RiskScoreBadge({
  score, className,
}: {
  score: 'low' | 'medium' | 'high' | 'critical';
  className?: string;
}) {
  const s = RISK_SCORE_STYLES[score];
  return (
    <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', s.color, className)}>
      {s.label}
    </Badge>
  );
}

// ─── Role source ────────────────────────────────────────────────────────────

/**
 * Map every value of the shared `RoleSource` enum (see
 * `mock-users-data.ts`) to a human-readable label and a colour token.
 * The component imports the shared enum type so the matrix here can
 * never drift from the API parsing site.
 *
 * Default fallback: an unknown source value (e.g. a new DB enum
 * variant the badge hasn't been updated for) renders the raw string
 * verbatim with the neutral muted style. This is the last-ditch
 * defense that closes #181 — prior code crashed on 'manual' because
 * the labels object had no entry for it.
 */
const ROLE_SOURCE_LABELS: Record<RoleSource, { label: string; color: string }> = {
  // Synthesised by the User adapter for the org-membership system role.
  direct:     { label: 'Direct',     color: 'bg-primary/10 text-primary border-primary/30' },
  // Inherited via department or group (mock-UI variants — the canonical
  // DB value is 'inherited' below; both render with the same chrome).
  department: { label: 'Department', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  user_group: { label: 'Group',      color: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30' },
  // SSO-mirrored from the IdP.
  sso:        { label: 'SSO',        color: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30' },
  // DB role_source values — `manual` is the default for an ad-hoc
  // admin assignment via POST /users/:id/roles; we render it with the
  // same chrome as `direct` so users see one stable label for "you
  // were assigned this directly".
  manual:     { label: 'Direct',     color: 'bg-primary/10 text-primary border-primary/30' },
  inherited:  { label: 'Inherited',  color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
};

export function RoleSourceBadge({
  source,
  className,
}: {
  source: RoleSource | string;
  className?: string;
}) {
  // Defensive lookup — if `source` is not a known RoleSource value
  // (could happen if the DB role_source enum gains a new variant
  // before the badge is updated) we fall back to a neutral chip
  // showing the raw string. Better than the prior crash from
  // `labels[source].label` on an undefined entry.
  const entry = (ROLE_SOURCE_LABELS as Record<string, { label: string; color: string } | undefined>)[source];
  const label = entry?.label ?? source;
  const color = entry?.color ?? 'bg-muted text-muted-foreground border-border';
  return (
    <Badge variant="outline" className={cn('text-[9px] px-1 py-0 h-4', color, className)}>
      {label}
    </Badge>
  );
}

// ─── Avatar ─────────────────────────────────────────────────────────────────

/**
 * User avatar with initials. Uses the user's deterministic color.
 */
export function UserAvatar({
  name, color = 'indigo', size = 'md', className,
}: {
  name: string;
  color?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  const sizeClasses = {
    xs: 'h-5 w-5 text-[9px]',
    sm: 'h-6 w-6 text-[10px]',
    md: 'h-8 w-8 text-xs',
    lg: 'h-10 w-10 text-sm',
  };
  const colorClass = `bg-${color}-500/15 text-${color}-700 dark:text-${color}-300`;

  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center font-semibold shrink-0 border border-border/50',
        sizeClasses[size],
        colorClass,
        className,
      )}
    >
      {initials}
    </div>
  );
}
